import * as THREE from 'three';
import { isWaterKind, type GameMap, type MapObject, type TrapState, type WaterKind, type Weather } from '@sunderfen/shared';
import { G, addMesh, freeTree } from './models.js';
import type { Glows } from './glows.js';
import type { Particles } from './particles.js';

const hash = (a: number, b: number) => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };

/** How each kind of water's spots look. */
const STYLE: Record<WaterKind | 'hidden', { ring: number; bob: number; speed: number; glow?: number; bubbles?: boolean; jump: [number, number] }> = {
  shore: { ring: 0xffffff, bob: 0xe8f4f8, speed: 0.75, jump: [6, 14] },
  river: { ring: 0xeaf8ff, bob: 0xdff0f4, speed: 0.95, jump: [5, 12] },
  lake: { ring: 0xffffff, bob: 0xe8f4f8, speed: 0.5, jump: [7, 16] },
  fen: { ring: 0xc2c98e, bob: 0x9aa070, speed: 0.38, bubbles: true, jump: [10, 20] },
  cove: { ring: 0x8ff6ff, bob: 0x8ff6ff, speed: 0.6, glow: 0x6fe8ff, jump: [8, 18] },
  quarry: { ring: 0xd8a080, bob: 0xc08a6a, speed: 0.45, jump: [8, 18] },
  tarn: { ring: 0xf0faff, bob: 0xe8f4f8, speed: 0.55, jump: [7, 16] },
  reservoir: { ring: 0xd8f0f0, bob: 0xc8e8e8, speed: 0.35, glow: 0xa8e0e0, jump: [9, 20] },
  sump: { ring: 0x9ad8c8, bob: 0x7ab8a8, speed: 0.3, glow: 0x6ae0c0, bubbles: true, jump: [12, 24] },
  hidden: { ring: 0xffe08a, bob: 0xffe9a8, speed: 0.55, glow: 0xffc860, jump: [4, 9] },
};

interface Spot { obj: MapObject; g: THREE.Group; rings: THREE.Mesh[]; bob: THREE.Mesh; style: (typeof STYLE)['lake']; glow?: THREE.Sprite; nextJump: number; nextBubble: number; hidden: boolean }
interface Jump { mesh: THREE.Mesh; t: number; x: number; z: number; dx: number; dz: number; size: number }

/**
 * Everything fishing draws in the world: spots (styled by water, drifting when the server
 * moves them), fish jumping, your own trap buoys, and rain.
 */
export class FishingFx {
  readonly spots = new Map<number, Spot>();
  /** Buoys are clickable; each carries userData.trap = index into self.traps. */
  readonly buoys: THREE.Group[] = [];
  private jumps: Jump[] = [];
  private fishPool: THREE.Mesh[] = [];
  private charts = new Set<number>();
  private rain: Rain;

  constructor(private scene: THREE.Scene, private map: GameMap, private glows: Glows, private particles: Particles, rainDrops: number) {
    const ringGeo = new THREE.RingGeometry(0.22, 0.3, 18); ringGeo.rotateX(-Math.PI / 2);
    for (const o of map.objects) {
      if (o.type !== 'resource' || !isWaterKind(o.kind)) continue;
      const hidden = !!o.props?.hidden, style = STYLE[hidden ? 'hidden' : o.kind];
      const g = new THREE.Group(); g.position.set(o.x + 0.5, 0.05, o.z + 0.5);
      const rings = [0, 1, 2].map(i => { const r = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color: style.ring, transparent: true, opacity: 0.6, depthWrite: false })); r.userData.phase = i / 3 + hash(o.x, o.z); g.add(r); return r; });
      const bob = new THREE.Mesh(G.oct(0.07), new THREE.MeshBasicMaterial({ color: style.bob })); g.add(bob);
      g.traverse((c: THREE.Object3D) => { c.userData.obj = o; });
      g.visible = !hidden;
      scene.add(g);
      const glow = style.glow ? glows.add(o.x + 0.5, 0.3, o.z + 0.5, style.glow, hidden ? 1.6 : 2.2, { base: hidden ? 0.35 : 0.2, night: 0.6, flicker: 0.3 }) : undefined;
      if (glow) glow.visible = !hidden;
      this.spots.set(o.id, { obj: o, g, rings, bob, style, glow, nextJump: 2 + hash(o.id, 3) * style.jump[1], nextBubble: 0, hidden });
    }
    const fishGeo = G.oct(0.12); fishGeo.scale(0.6, 0.6, 1.8);
    for (let i = 0; i < 6; i++) { const m = new THREE.Mesh(fishGeo, new THREE.MeshLambertMaterial({ color: 0xb8c4c8 })); m.visible = false; scene.add(m); this.fishPool.push(m); }
    this.rain = new Rain(scene, rainDrops);
  }

  /** Is a spot visible to this player (hidden ones need a chart)? */
  visible(id: number) { const s = this.spots.get(id); return !!s && (!s.hidden || this.charts.has(id)); }

  setCharts(ids: number[]) {
    this.charts = new Set(ids);
    for (const s of this.spots.values()) if (s.hidden) { const v = this.charts.has(s.obj.id); s.g.visible = v; if (s.glow) s.glow.visible = v; }
  }

  /** The server moved a spot. Fade it across with a splash at both ends. */
  move(id: number, x: number, z: number) {
    const s = this.spots.get(id);
    if (!s || (s.obj.x === x && s.obj.z === z)) return;
    this.particles.splash(s.obj.x + 0.5, 0.05, s.obj.z + 0.5);
    this.map.moveObject(s.obj, x, z);
    s.g.position.set(x + 0.5, 0.05, z + 0.5);
    s.glow?.position.set(x + 0.5, 0.3, z + 0.5);
    this.particles.splash(x + 0.5, 0.05, z + 0.5, true);
  }

  setTraps(traps: TrapState[]) {
    for (const b of this.buoys) { this.scene.remove(b); freeTree(b); }
    this.buoys.length = 0;
    traps.forEach((t, i) => {
      const g = new THREE.Group();
      g.position.set(t.x + 0.5, 0, t.z + 0.5);
      const pot = t.tool === 'crab_pot';
      addMesh(g, G.cyl(0.11, 0.13, 0.16, 8), pot ? 0xe0603a : 0xd83a2a, 0, 0.05, 0);
      addMesh(g, G.cyl(0.11, 0.11, 0.06, 8), 0xf4f0e6, 0, 0.16, 0);
      addMesh(g, G.cyl(0.015, 0.015, 0.34, 4), 0x3a3a3a, 0, 0.32, 0);
      addMesh(g, G.box(0.14, 0.09, 0.01), pot ? 0xf1bf55 : 0x2f7f6f, 0.07, 0.44, 0);
      g.traverse((c: THREE.Object3D) => { c.userData.trap = i; });
      g.userData.phase = hash(t.x, t.z) * 6;
      this.scene.add(g);
      this.buoys.push(g);
    });
  }

  setWeather(w: Weather) { this.rain.target = w === 'rain' ? 1 : 0; }
  get wet() { return this.rain.amount; }

  update(dt: number, time: number, tx: number, tz: number, night: number) {
    for (const s of this.spots.values()) {
      if (!s.g.visible) continue;
      // spots far out in the fog aren't drawn at all
      const far = Math.abs(s.g.position.x - tx) > 70 || Math.abs(s.g.position.z - tz) > 70;
      if (far !== !!s.g.userData.far) { s.g.userData.far = far; for (const c of s.g.children) c.visible = !far; }
      const near = Math.abs(s.g.position.x - tx) < 34 && Math.abs(s.g.position.z - tz) < 34;
      if (!near) continue;
      s.rings.forEach(r => { const ph = (time * s.style.speed + r.userData.phase) % 1; r.scale.setScalar(0.6 + ph * 2.2); (r.material as THREE.MeshBasicMaterial).opacity = 0.65 * (1 - ph); });
      s.bob.position.y = 0.05 + Math.sin(time * 3 + s.g.position.x) * 0.04;
      if (s.style.bubbles && time > s.nextBubble) {
        s.nextBubble = time + 0.4 + Math.random() * 0.9;
        this.particles.emit({ x: s.g.position.x + (Math.random() - 0.5) * 0.6, y: 0.02, z: s.g.position.z + (Math.random() - 0.5) * 0.6, count: 1, vy: 0.35, vSpread: 0.1, colors: [0xe8f0d8], size: 0.05, life: 0.5, alpha: 0.7 });
      }
      s.nextJump -= dt * (1 + this.rain.amount * 0.8) * (night > 0.5 && s.obj.kind === 'cove' ? 1.6 : 1);
      if (s.nextJump <= 0) { s.nextJump = s.style.jump[0] + Math.random() * (s.style.jump[1] - s.style.jump[0]); this.jump(s); }
    }
    for (let i = this.jumps.length - 1; i >= 0; i--) {
      const j = this.jumps[i];
      j.t += dt / 0.75;
      if (j.t >= 1) { j.mesh.visible = false; this.particles.splash(j.x + j.dx, 0.04, j.z + j.dz); this.fishPool.push(j.mesh); this.jumps.splice(i, 1); continue; }
      const y = Math.sin(j.t * Math.PI) * 0.55 * j.size;
      j.mesh.position.set(j.x + j.dx * j.t, y - 0.05, j.z + j.dz * j.t);
      j.mesh.rotation.set(-Math.cos(j.t * Math.PI) * 1.1, Math.atan2(j.dx, j.dz), 0);
    }
    for (const b of this.buoys) { b.position.y = Math.sin(time * 2.2 + b.userData.phase) * 0.035; b.rotation.z = Math.sin(time * 1.7 + b.userData.phase) * 0.08; }
    this.rain.update(dt, tx, tz, this.map, this.particles);
  }

  private jump(s: Spot) {
    const mesh = this.fishPool.pop();
    if (!mesh) return;
    const a = Math.random() * Math.PI * 2, x = s.g.position.x + (Math.random() - 0.5) * 0.8, z = s.g.position.z + (Math.random() - 0.5) * 0.8;
    const size = s.obj.kind === 'river' || s.obj.kind === 'shore' ? 1 : 1.25;
    (mesh.material as THREE.MeshLambertMaterial).color.set(s.obj.kind === 'cove' ? 0x8ff6ff : s.obj.kind === 'fen' ? 0x8a8a52 : 0xb8c4c8);
    mesh.scale.setScalar(size);
    mesh.visible = true;
    this.particles.splash(x, 0.04, z);
    this.jumps.push({ mesh, t: 0, x, z, dx: Math.sin(a) * 0.7, dz: Math.cos(a) * 0.7, size });
  }
}

/** Rain streaks that follow the camera, plus little splashes on the water. */
class Rain {
  amount = 0;
  target = 0;
  private lines: THREE.LineSegments;
  private pos: Float32Array;
  private speed: Float32Array;
  private splashClock = 0;
  constructor(scene: THREE.Scene, private n: number) {
    this.pos = new Float32Array(n * 6);
    this.speed = new Float32Array(n);
    for (let i = 0; i < n; i++) { this.speed[i] = 14 + Math.random() * 6; this.seed(i, 0, 0, Math.random() * 16); }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    this.lines = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0xdce6ee, transparent: true, opacity: 0, depthWrite: false }));
    this.lines.frustumCulled = false;
    this.lines.visible = false;
    scene.add(this.lines);
  }
  private seed(i: number, tx: number, tz: number, y = 14) {
    const x = tx + (Math.random() - 0.5) * 36, z = tz + (Math.random() - 0.5) * 36, k = i * 6;
    this.pos[k] = x; this.pos[k + 1] = y; this.pos[k + 2] = z;
    this.pos[k + 3] = x + 0.06; this.pos[k + 4] = y - 0.75; this.pos[k + 5] = z + 0.03;
  }
  update(dt: number, tx: number, tz: number, map: GameMap, particles: Particles) {
    this.amount += (this.target - this.amount) * Math.min(1, dt * 0.25);
    const on = this.amount > 0.02;
    this.lines.visible = on;
    if (!on) return;
    (this.lines.material as THREE.LineBasicMaterial).opacity = 0.62 * this.amount;
    const active = Math.floor(this.n * this.amount);
    for (let i = 0; i < this.n; i++) {
      const k = i * 6;
      if (i >= active) { this.pos[k + 1] = this.pos[k + 4] = -50; continue; }
      const d = this.speed[i] * dt;
      this.pos[k + 1] -= d; this.pos[k + 4] -= d; this.pos[k] += d * 0.08; this.pos[k + 3] += d * 0.08;
      const x = this.pos[k], z = this.pos[k + 2];
      const ground = map.inBounds(Math.floor(x), Math.floor(z)) ? Math.max(0, map.heightAt(x, z)) : 0;
      if (this.pos[k + 4] < ground || Math.abs(x - tx) > 20 || Math.abs(z - tz) > 20) this.seed(i, tx, tz, ground + 10 + Math.random() * 6);
    }
    (this.lines.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.splashClock -= dt;
    if (this.splashClock <= 0) {
      this.splashClock = 0.05 / Math.max(0.2, this.amount);
      const x = tx + (Math.random() - 0.5) * 22, z = tz + (Math.random() - 0.5) * 22;
      if (map.inBounds(Math.floor(x), Math.floor(z)) && map.heightAt(x, z) < 0) particles.emit({ x, y: 0.03, z, count: 2, spread: 0.05, vy: 0.9, vSpread: 0.4, colors: [0xe8f4ff], size: 0.035, life: 0.25, gravity: 9, alpha: 0.7 });
    }
  }
}
